/**
 * auth.register：新用户注册埋点（51 只报事件名）。
 *
 * 判定与登录后新手引导一致：
 * 1) createTime 在注册时间窗口内
 * 2) 尚未领取试用金（且无 TRIAL 余额）
 *
 * profile 首次失败时不放弃：后续任意 getUserProfile 成功会经 noteUserProfileLoaded 再试，
 * 打出后走 LA Outbox 自动上报。
 */
import { REGISTER_REWARD_MAX_AGE_MS } from '@/constants/register-reward';
import { parseProfileCreateTimeMs } from '@/utils/register-reward-eligibility';
import { getUserId, hasAuthSession } from '@/utils/auth-token';
import { trackAnalyticsLog, withAcquisitionProps } from '@/utils/analytics-track';

/** 分析窗口略宽于奖励弹窗 2min，降低 profile / 试用金接口延迟漏报 */
export const AUTH_REGISTER_ANALYTICS_MAX_AGE_MS = Math.max(
  REGISTER_REWARD_MAX_AGE_MS,
  10 * 60 * 1000
);

let noteInflight = false;

/**
 * @param {{ createTime?: unknown, hasTrialFund?: boolean }} input
 * @returns {boolean} 是否打出本次事件
 */
export function tryTrackAuthRegisterOnce(input = {}) {
  if (input.hasTrialFund) return false;

  const createdMs = parseProfileCreateTimeMs(input.createTime);
  if (createdMs == null) return false;
  const ageMs = Date.now() - createdMs;
  if (ageMs < 0 || ageMs > AUTH_REGISTER_ANALYTICS_MAX_AGE_MS) return false;

  const uid = String(getUserId() || '').trim();
  const registerKey = uid ? `ds-analytics-auth-register:${uid}` : '';
  const durable =
    typeof localStorage !== 'undefined'
      ? localStorage
      : typeof sessionStorage !== 'undefined'
      ? sessionStorage
      : null;
  if (registerKey && durable?.getItem(registerKey) === '1') return false;

  trackAnalyticsLog({
    key: 'auth.register',
    label: '用户注册',
    value: withAcquisitionProps({
      userId: uid || undefined,
      createTime: input.createTime ?? undefined,
      hasTrialFund: false,
    }),
  });

  if (registerKey && durable) {
    try {
      durable.setItem(registerKey, '1');
    } catch {
      /* ignore */
    }
  }
  return true;
}

/**
 * @param {unknown} claimed
 * @returns {boolean}
 */
export function resolveHasTrialFund(claimed) {
  return Boolean(claimed);
}

/**
 * 任意路径拿到 profile 后调用：关键字段齐了再打 auth.register（经 Outbox 上报 51）。
 * @param {{ createTime?: unknown } | null | undefined} profile
 * @returns {Promise<boolean>}
 */
export async function noteUserProfileLoaded(profile) {
  if (!profile || !hasAuthSession()) return false;
  if (noteInflight) return false;
  noteInflight = true;
  try {
    let hasTrialFund = false;
    try {
      const { useTrialFundStore } = await import('@/stores/trialFund');
      const store = useTrialFundStore();
      if (store.conditions == null) {
        try {
          await store.refreshConditions();
        } catch {
          /* 条件失败时按未领试用金尝试；老用户通常 createTime 已出窗 */
        }
      }
      hasTrialFund = resolveHasTrialFund(store.conditions?.claimed);
    } catch {
      /* Pinia 未就绪 */
    }
    return tryTrackAuthRegisterOnce({
      createTime: profile.createTime,
      hasTrialFund,
    });
  } finally {
    noteInflight = false;
  }
}
